import { useState } from 'react';
import { useReduxState } from 'hooks';
import { Button, SearchForm, SortBy } from 'components/common';
import { StatRow } from 'components/common/stats';
import { breakpoints } from 'styles';

interface Props {
  countData: { [key: string]: number };
  emptyMessage: string;
  percentagesData: { [key: string]: number };
  label: string;
  numberOfUsers: number;
  selectedItems: string[];
  setSelectedItems: (value: string[]) => void;
  showPercentages: boolean;
}

const AnalyticsCompetenciesList = ({
  countData,
  emptyMessage,
  percentagesData,
  label,
  numberOfUsers,
  selectedItems,
  setSelectedItems,
  showPercentages
}: Props) => {
  const { messages, theme } = useReduxState();

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('value');
  const [scrollable, setScrollable] = useState(false);

  const handleAddItem = (item) => {
    setSelectedItems([...selectedItems, item]);
  };

  const handleScrollable = (el: HTMLDivElement) => {
    if (el) setScrollable(el.scrollHeight > el.clientHeight);
  };

  const data = showPercentages ? percentagesData : countData;

  const sortedData = Object.keys(data)
    .filter(
      (item) =>
        !search || item.toLowerCase().indexOf(search.toLowerCase()) !== -1
    )
    .map((item) => ({
      title: item,
      value: data[item],
      count: countData[item],
      percentage: percentagesData[item]
    }));
  if (sortBy === 'title')
    sortedData.sort((a, b) => (a.title > b.title ? 1 : -1));

  return (
    <div className="list">
      <div className="header">
        <div className="title">{label}</div>

        <div className="filters">
          <div style={{ width: 200 }}>
            <SearchForm
              onClear={() => setSearch('')}
              onSearch={(value: string) => setSearch(value)}
              placeholderText={messages.commonSearch}
              size="header"
            />
          </div>

          <SortBy
            onChange={({ sortBy }: { sortBy: string }) => setSortBy(sortBy)}
            options={[
              { text: messages.commonValue, value: 'value' },
              { text: messages.commonTitle, value: 'title' }
            ]}
            value={sortBy}
          />
        </div>
      </div>
      <div className="list-items" ref={handleScrollable}>
        {sortedData.length ? (
          sortedData.map((entry, index) => (
            <StatRow
              key={entry.title}
              label={`${index + 1}. ${entry.title}`}
              value={
                <div className="list-value">
                  <span>
                    {`${entry.value}${showPercentages ? ' %' : ''}`}
                    {showPercentages ? (
                      <span
                        style={{
                          color: theme.textColorLighter,
                          fontWeight: 600,
                          marginLeft: 5
                        }}
                      >{`(${entry.count}/${numberOfUsers})`}</span>
                    ) : null}
                  </span>

                  <Button
                    category="light"
                    disabled={selectedItems.includes(entry.title)}
                    icon="plus"
                    iconStyle={{ margin: 4 }}
                    onClick={() => handleAddItem(entry.title)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: 20,
                      width: 20
                    }}
                    tooltip={messages.analyticsGraphAdd}
                  />
                </div>
              }
            />
          ))
        ) : (
          <div className="empty">{emptyMessage}</div>
        )}

        {scrollable ? (
          <div className="more">{messages.commonScrollForMore}</div>
        ) : null}
      </div>
      <style jsx>{`
        .list {
          padding: 15px;
          position: relative;
        }
        .title {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          font-weight: 700;
          padding: 10px;
        }
        .filters {
          align-items: center;
          display: flex;
          gap: 10px;
          padding: 0 10px;
        }
        .list-items {
          margin-top: 10px;
          max-height: 250px;
          overflow-y: scroll;
          width: 100%;
        }
        .list-value {
          align-items: center;
          display: flex;
          gap: 15px;
        }
        .more {
          background-color: ${theme.lightGrey};
          border-radius: 10px;
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          margin-left: auto;
          margin-right: auto;
          opacity: 50%;
          padding: 2px 5px;
          width: max-content;
          position: absolute;
          bottom: 3%;
          right: 0;
          left: 0;
        }
        .empty {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          margin: 5px 10px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .header {
            align-items: center;
            display: flex;
            justify-content: space-between;
          }
        }
      `}</style>
    </div>
  );
};

export default AnalyticsCompetenciesList;
